import { ADYEN_RESUME_CONSTANTS } from '../constants/adyen-resume.constants';
import { WIDGET_CONSTANTS } from '../constants/widget-constants';
import { AdyenResumeContext, AdyenResumeHost, AdyenResumeRecord, AdyenReturnParams } from '../models/adyen-resume.model';

const PARAMS = ADYEN_RESUME_CONSTANTS.URL_PARAMS;

/** What Adyen (or the Calenso return page) put on the page URL, or null when it is not a return. */
export function readAdyenReturnParams(search: string): AdyenReturnParams | null {
  let query: URLSearchParams;
  try {
    query = new URLSearchParams(search);
  } catch {
    return null;
  }
  const redirectResult = query.get(PARAMS.REDIRECT_RESULT) || undefined;
  const hostedNonce = query.get(PARAMS.HOSTED_NONCE) || undefined;
  if (!redirectResult && !hostedNonce) {
    return null;
  }

  return {
    redirectResult,
    sessionId: redirectResult ? (query.get(PARAMS.SESSION_ID) || undefined) : undefined,
    hostedNonce,
  };
}

/**
 * The page URL without anything a payment return put on it.
 *
 * It is the base of every `return_url` the widget sends, so a stale `redirectResult` left on it
 * would travel into the NEXT session's return and be replayed. `sessionId` is a common name, so it
 * is removed only beside a `redirectResult` - a partner page may use it for its own purpose.
 */
export function stripAdyenReturnParams(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  if (url.searchParams.has(PARAMS.REDIRECT_RESULT)) {
    url.searchParams.delete(PARAMS.REDIRECT_RESULT);
    url.searchParams.delete(PARAMS.SESSION_ID);
  }
  url.searchParams.delete(PARAMS.HOSTED_NONCE);

  return url.toString();
}

/** True when the return on the URL belongs to the payment this record parked. */
export function adyenReturnMatchesRecord(params: AdyenReturnParams, record: AdyenResumeRecord): boolean {
  if (record.kind === 'dropin') {
    return !!params.redirectResult
      // Adyen names the session it returns for; a different one is another payment's return.
      && (params.sessionId === undefined || params.sessionId === record.sessionId);
  }

  return !!params.hostedNonce && !!record.nonce && params.hostedNonce === record.nonce;
}

/** A record older than its session can still be completed with. */
export function isAdyenResumeRecordExpired(record: AdyenResumeRecord, now: number): boolean {
  const ttl = record.kind === 'hosted' ? ADYEN_RESUME_CONSTANTS.HOSTED_TTL_MS : ADYEN_RESUME_CONSTANTS.DROPIN_TTL_MS;

  return !Number.isFinite(record.createdAt) || now - record.createdAt > ttl;
}

/**
 * The payment was taken for the booking the returning host is about to make - the same booking
 * scope and the same number of items. Anything else must not be booked on this payment.
 */
export function adyenResumeContextMatches(record: AdyenResumeContext, current: AdyenResumeContext): boolean {
  return record.holdScope === current.holdScope && record.items === current.items;
}

/** The next wait between two reads of a hosted result: 2 s, growing to 10 s. */
export function nextAdyenPollDelay(previous: number | undefined): number {
  if (!previous) {
    return ADYEN_RESUME_CONSTANTS.POLL_INITIAL_MS;
  }

  return Math.min(ADYEN_RESUME_CONSTANTS.POLL_MAX_MS, Math.round(previous * ADYEN_RESUME_CONSTANTS.POLL_BACKOFF_FACTOR));
}

/** Only a structurally complete record is resumed; anything else is someone else's or corrupt. */
export function isAdyenResumeRecord(value: unknown, partnerId: number): value is AdyenResumeRecord {
  const record = value as Partial<AdyenResumeRecord> | null;

  return !!record
    && typeof record === 'object'
    && record.v === 1
    && record.partnerId === partnerId
    && (record.kind === 'dropin' || record.kind === 'hosted')
    && typeof record.sessionId === 'string' && record.sessionId !== ''
    && typeof record.holdScope === 'string'
    && typeof record.items === 'number'
    && !!record.snapshot && typeof record.snapshot === 'object'
    && typeof record.snapshot.host === 'string'
    && (record.kind !== 'hosted' || (typeof record.nonce === 'string' && typeof record.token === 'string'))
    && (record.kind !== 'dropin' || typeof record.clientKey === 'string');
}

/**
 * The booking type the widget must open so the host that was paying renders again, or null when this
 * widget cannot show that host (then nothing can be resumed). A widget opens its FIRST enabled type,
 * which is not necessarily the one the shopper paid on.
 *
 * The appointment host also serves a tour: a tour hands its combination to the appointment flow.
 */
export function adyenResumeBookingType(
  host: AdyenResumeHost,
  bookTypesEnabled: string[],
  isCompactMode: boolean,
): string | null {
  if (host === 'compact' || isCompactMode) {
    // The compact widget renders whatever the booking type; nothing else renders beside it.
    return host === 'compact' && isCompactMode ? (bookTypesEnabled[0] ?? WIDGET_CONSTANTS.APPOINTMENT) : null;
  }
  const type = {
    appointment: WIDGET_CONSTANTS.APPOINTMENT,
    event: WIDGET_CONSTANTS.EVENT,
    pds: WIDGET_CONSTANTS.PRE_DEFINED_SLOT,
  }[host];
  const enabled = bookTypesEnabled.includes(type)
    || (type === WIDGET_CONSTANTS.APPOINTMENT && bookTypesEnabled.includes(WIDGET_CONSTANTS.TOUR));

  return enabled ? type : null;
}
